import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { X, Plus, Trash2, CheckIcon, Mail } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import {
  ConnectionResponse,
  InviteUserInformation,
} from "@/models/user-management/UserManagement";
import { Input } from "@capacit-com/lara-law-shared-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import { TenantRole } from "@/models/tenant/tenant";
import { useUserManagementContext } from "@/providers/user-management/UserManagementContext";
import { Label } from "@capacit-com/lara-law-shared-ui";
import useUserManagement from "@/hooks/useUserManagement";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandInput,
  CommandList,
} from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { InvitedUser } from "@/models/users/User";

/** Days left on an invitation, falling back to the default when it has expired. */
const daysValidityFor = (expiresAt: string | Date): number => {
  const diffTime = new Date(expiresAt).getTime() - Date.now();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 7;
};

type InviteUserDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userToEdit?: InvitedUser | null;
  setUserToEdit: (user: InvitedUser | null) => void;
};

export const InviteUserDialog: FC<InviteUserDialogProps> = ({
  open,
  onOpenChange,
  userToEdit,
  setUserToEdit,
}) => {
  const { tenant } = useUserManagementContext();
  const { inviteUsers, useQueryConnectionDetails } = useUserManagement();
  const connectionDetailsQuery = useQueryConnectionDetails();

  const [userInviteDetails, setUserInviteDetails] = useState<
    InviteUserInformation[]
  >([{ email: "", roles: [] }]);
  const [connectionId, setConnectionId] = useState<string>("");
  const [daysValidity, setDaysValidity] = useState<number>(7);
  const [connectionDetails, setConnectionDetails] = useState<
    ConnectionResponse[] | undefined
  >();

  // Snapshot of the form as it opened (per invite / fresh invite), so the
  // discard guard only asks when the user actually changed something.
  const openedWith = useMemo(
    () =>
      JSON.stringify({
        users: userToEdit
          ? [
              {
                email: userToEdit.invitee.email,
                roles: userToEdit.roles.map((r) => r.externalName),
              },
            ]
          : [{ email: "", roles: [] }],
        daysValidity: userToEdit ? daysValidityFor(userToEdit.expiresAt) : 7,
      }),
    [userToEdit]
  );
  const current = JSON.stringify({
    users: userInviteDetails.map((u) => ({
      email: u.email,
      roles: u.roles.map((r) => r.externalName),
    })),
    daysValidity,
  });
  const isDirty = open && current !== openedWith;

  useEffect(() => {
    if (connectionDetailsQuery.isFetched && connectionDetailsQuery.data) {
      setConnectionDetails(connectionDetailsQuery.data);
      // Set the first connection as default if none is selected
      if (!connectionId && connectionDetailsQuery.data.length > 0) {
        setConnectionId(connectionDetailsQuery.data[0].connectionId);
      }
    }
  }, [
    connectionDetailsQuery.isFetched,
    connectionDetailsQuery.data,
    connectionId,
  ]);

  // Populate form when userToEdit is provided
  useEffect(() => {
    if (userToEdit && open) {
      setUserInviteDetails([
        {
          email: userToEdit.invitee.email,
          roles: userToEdit.roles,
        },
      ]);
      setConnectionId(userToEdit.connectionId);
      setDaysValidity(daysValidityFor(userToEdit.expiresAt));
    } else if (!userToEdit && open) {
      // Reset form when opening without userToEdit
      setUserInviteDetails([{ email: "", roles: [] }]);
      setDaysValidity(7);
    }
  }, [userToEdit, open]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setUserInviteDetails([{ email: "", roles: [] }]);
      setDaysValidity(7);
      setUserToEdit(null);
    }
    onOpenChange(isOpen);
  };

  const handleAddUser = () => {
    // Don't allow adding users when editing
    if (userToEdit) return;
    setUserInviteDetails([...userInviteDetails, { email: "", roles: [] }]);
  };

  const handleRemoveUser = (index: number) => {
    if (userInviteDetails.length > 1) {
      const updated = userInviteDetails.filter((_, i) => i !== index);
      setUserInviteDetails(updated);
    }
  };

  const handleEmailChange = (index: number, email: string) => {
    const updated = [...userInviteDetails];
    updated[index] = { ...updated[index], email };
    setUserInviteDetails(updated);
  };

  const handleRolesChange = (index: number, roles: TenantRole[]) => {
    const updated = [...userInviteDetails];
    updated[index] = { ...updated[index], roles };
    setUserInviteDetails(updated);
  };

  const daysToSeconds = (days: number) => days * 24 * 60 * 60;

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const isInviteButtonDisabled = (): boolean => {
    const validUsers = userInviteDetails.filter(
      (user) => user.email.trim() !== "" && isValidEmail(user.email)
    );
    return validUsers.length === 0 || !connectionId || inviteUsers.isPending;
  };

  const onConfirm = async () => {
    const validUsers = userInviteDetails.filter(
      (user) => user.email.trim() !== "" && isValidEmail(user.email)
    );

    if (validUsers.length === 0 || !connectionId || daysValidity <= 0) return;

    try {
      if (userToEdit) {
        await inviteUsers.mutateAsync({
          invitedUsersList: [
            {
              userId: userToEdit.id,
              email: validUsers[0].email.trim(),
              roles: validUsers[0].roles,
              isReinvite: true,
            },
          ],
          connectionId,
          expireDateInSeconds: daysToSeconds(daysValidity),
        });
      } else {
        await inviteUsers.mutateAsync({
          invitedUsersList: validUsers.map((user) => ({
            email: user.email.trim(),
            roles: user.roles,
          })),
          connectionId,
          expireDateInSeconds: daysToSeconds(daysValidity),
        });
      }

      handleOpenChange(false);
      setUserInviteDetails([{ email: "", roles: [] }]);
      setDaysValidity(7);
      setUserToEdit(null);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes("User already exists")
      ) {
        const messageParts = error.message.split(":");
        const existingEmails =
          messageParts.length > 1 ? messageParts[1].trim() : "";
        const emailCount = existingEmails.split(",").length;
        const message =
          emailCount === 1
            ? `Brugeren eksisterer allerede: ${existingEmails}`
            : `Følgende brugere eksisterer allerede: ${existingEmails}`;
        showErrorToast(message);
      } else if (
        error instanceof Error &&
        error.message.toLowerCase().includes("role not allowed")
      ) {
        showErrorToast("Du kan ikke ændre eller tilføje system roller.");
      } else {
        showErrorToast(
          "Der skete en fejl ved invitation af brugere - verificer om det er den korrekte email adresse."
        );
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isInviteButtonDisabled()) {
      e.preventDefault();
      onConfirm();
    }
  };

  const cleanConnectionName = (name: string) => {
    if (name.toLowerCase().includes("loda")) {
      name.replace("-LODA", "");
    }
    if (name.toLowerCase().includes("microsoft")) {
      name = name + " SSO";
    }
    if (name.includes("-")) {
      name = name.split("-").join(" ");
    }
    return name;
  };
  const isSystemRole = (role: TenantRole) => {
    return role.externalName.toLowerCase().includes("system");
  };
  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      isDirty={isDirty}
      size="lg"
    >
      <DialogHeader>
        <DialogTitle>
          {userToEdit ? "Opdater invitation" : "Inviter bruger"}
        </DialogTitle>
        <DialogDescription>
          {userToEdit
            ? "Opdater invitationen for brugeren"
            : "Inviter brugere til organisationen"}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        {/* Connection Select */}
        <div className="flex flex-row gap-2">
          <div className="space-y-2 flex flex-col flex-1">
            <Label htmlFor="connection-select">Forbindelse</Label>
            <Select
              value={connectionId}
              onValueChange={setConnectionId}
              disabled={
                !connectionDetails ||
                connectionDetails.length === 0 ||
                !!userToEdit
              }
            >
              <SelectTrigger id="connection-select" className="w-full">
                <SelectValue
                  placeholder={`
                    ${
                      !connectionDetails || connectionDetails.length === 0
                        ? "Ingen forbindelser fundet"
                        : "Vælg forbindelse"
                    }
                  `}
                />
              </SelectTrigger>
              <SelectContent>
                {connectionDetails?.map((connection) => (
                  <SelectItem
                    key={connection.connectionId}
                    value={connection.connectionId}
                  >
                    {cleanConnectionName(connection.connection.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Days Validity Input */}
          <div className="space-y-2 flex flex-col flex-1">
            <Label htmlFor="days-validity">
              Antal dage invitationen er gyldig
            </Label>
            <Input
              id="days-validity"
              type="number"
              placeholder="Antal dage"
              value={daysValidity || ""}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (isNaN(value)) value = 0;
                if (value < 1) value = 1;
                if (value > 30) value = 30;
                setDaysValidity(value);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        {/* User Invite Details */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            {userToEdit && (
              <div className="text-sm p-4 bg-accent text-accent-foreground rounded-md w-full">
                <p>
                  Vær opmærksom på, at brugeren allerede er inviteret til
                  organisationen.
                  <br />
                  Ved at opdatere invitationen, vil en ny invitation blive sendt
                  til brugeren, og deres gamle invitation vil ikke virke
                  længere.
                  <br />
                  <br />
                  Det er ikke muligt at ændre email eller forbindelsen. Hvis
                  emailen er forkert, slet den nuværende invitation og opret en
                  ny.
                </p>
              </div>
            )}
            {!userToEdit && (
              <>
                <Label>Brugere</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddUser}
                  id="add-user-button"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Tilføj bruger
                </Button>
              </>
            )}
          </div>

          {userInviteDetails.map((userDetail, index) => (
            <div
              key={index}
              className="space-y-4 p-4 border rounded-md bg-accent/50"
            >
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  {userToEdit ? "Bruger" : `Bruger ${index + 1}`}
                </Label>
                {!userToEdit && userInviteDetails.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveUser(index)}
                    id={`remove-user-${index}`}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>

              {/* Email Input */}
              <div className="flex flex-col gap-2">
                <div className="space-y-2 mb-2">
                  <Label htmlFor={`email-input-${index}`}>Email</Label>
                  <Input
                    id={`email-input-${index}`}
                    type="email"
                    placeholder="Email"
                    value={userDetail.email}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={!!userToEdit}
                  />
                </div>

                {/* Roles Multi-Select */}
                <div className="space-y-2">
                  <Label>Roller</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        Vælg roller
                        {userDetail.roles.length > 0 && (
                          <>
                            <div className="mx-2 h-6 w-px bg-muted" />
                            {userDetail.roles.map((role) => (
                              <Badge
                                key={role.externalName}
                                variant="secondary"
                              >
                                {role.displayName}
                              </Badge>
                            ))}
                          </>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Søg roller..." />
                        <CommandList>
                          <CommandEmpty>Ingen resultater.</CommandEmpty>
                          <CommandGroup>
                            {tenant?.roles?.map((role) => {
                              const isSelected = userDetail.roles.some(
                                (r) => r.externalName === role.externalName
                              );
                              return (
                                <CommandItem
                                  key={role.externalName}
                                  onSelect={() => {
                                    const updatedRoles = isSelected
                                      ? userDetail.roles.filter(
                                          (r) =>
                                            r.externalName !== role.externalName
                                        )
                                      : [...userDetail.roles, role];
                                    handleRolesChange(index, updatedRoles);
                                  }}
                                  disabled={isSystemRole(role)}
                                >
                                  <div
                                    className={cn(
                                      "mr-2 flex size-4 items-center justify-center rounded-sm border border-primary",
                                      isSelected
                                        ? "bg-primary text-primary-foreground"
                                        : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <CheckIcon className="size-4 text-primary-foreground" />
                                  </div>
                                  <span>{role.displayName}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogBody>
      <DialogFooter>
        <FormDialogCancel>
          <X className="w-4 h-4 mr-1" />
          Annuller
        </FormDialogCancel>
        <Button onClick={onConfirm} disabled={isInviteButtonDisabled()}>
          {inviteUsers.isPending ? (
            <Spinner className="text-primary-foreground w-4 h-4 mr-1" />
          ) : (
            <Mail className="w-4 h-4 mr-1" />
          )}
          {inviteUsers.isPending
            ? userToEdit
              ? "Opdaterer"
              : "Sender"
            : userToEdit
              ? "Opdater"
              : "Send"}{" "}
          {!userToEdit &&
            (userInviteDetails.length === 1 ? "invitation" : "invitationer")}
          {inviteUsers.isPending && !userToEdit ? "..." : ""}
        </Button>
      </DialogFooter>
    </FormDialog>
  );
};
